<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref, watch } from 'vue';

import type { TranslatedField } from '@/types/filters';

import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';

type Option = { id: string; name: string; count: number; type: string; translatedName: Array<TranslatedField> };

const props = defineProps<{
  options: Array<Option>;
  placeholder: string;
  alignment?: 'left' | 'right';
}>();
const emit = defineEmits(['update:modelValue']);

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const globalConfigurationStore = useGlobalConfigurationStore();
const { currentLocale } = storeToRefs(globalConfigurationStore);

const isOpen = ref(false);
const dropdown = ref();
const selectedOptions = ref<Array<Option>>([]);
const keyboardIndex = ref(0);
const position = computed(() => props.alignment ? `${props.alignment}:0;top:110%;` : 'left:0;top:110%;');
const debounce = ref();

function openDropdown() {
  if (isOpen.value)
    return;
  isOpen.value = true;
  setTimeout(() => {
    isOpen.value === true ? document?.addEventListener('click', handleOutsideClick) : document?.removeEventListener('click', handleOutsideClick);
    isOpen.value === true ? document?.addEventListener('keydown', handleKeyboardNavigation) : document?.removeEventListener('keydown', handleKeyboardNavigation);
  }, 100);
}

function closeDropdown() {
  isOpen.value = false;
  setTimeout(() => {
    isOpen.value === true ? document?.addEventListener('click', handleOutsideClick) : document?.removeEventListener('click', handleOutsideClick);
    isOpen.value === true ? document?.addEventListener('keydown', handleKeyboardNavigation) : document?.removeEventListener('keydown', handleKeyboardNavigation);
  }, 100);
}

function handleOutsideClick(event: Event) {
  const path = event.composedPath();
  const target = path[0];
  const dropdown = document.querySelector('yb-list')?.shadowRoot?.getElementById('places_dropdown');
  // @ts-expect-error typeof target
  if (!dropdown?.contains(target)) {
    closeDropdown();
  }
}

function handleKeyboardNavigation(event: Event) {
  event.preventDefault();
  const e = event as KeyboardEvent;
  if (e.key === 'Escape' || e.key === 'Tab') {
    closeDropdown();
    return;
  }
  const options = dropdown.value.querySelectorAll('.option-row');
  if (e.key === 'Enter') {
    options?.[keyboardIndex.value].querySelector('.option')?.click();
    return;
  }
  if (e.key === 'ArrowDown') {
    keyboardIndex.value = keyboardIndex.value + 1 === props.options?.length ? 0 : keyboardIndex.value + 1;
  }
  else if (e.key === 'ArrowUp') {
    keyboardIndex.value = keyboardIndex.value - 1 < 0 ? props.options.length - 1 : keyboardIndex.value - 1;
  }
  options?.[keyboardIndex.value].focus();
}

function selectOption(option: Option) {
  if (debounce.value) {
    clearTimeout(debounce.value);
  }
  if (selectedOptions.value.some(it => it.name === option.name)) {
    selectedOptions.value = selectedOptions.value.filter(item => item.name !== option.name);
  }
  else {
    selectedOptions.value.push(option);
  }
  debounce.value = setTimeout(() => {
    emit('update:modelValue', selectedOptions.value);
  }, 750);
}

function refreshSelectedOptions() {
  const savedPlaces = filters.value.selections.find(it => it.filterProperties?.type === 'taxonomyPlaces')?.selection ?? [];
  selectedOptions.value = selectedOptions.value.filter(opt =>
    savedPlaces.includes(opt.id),
  );
  if (savedPlaces.length > 0) {
    for (const place of savedPlaces) {
      const matchingOption = props.options.find(it => it.id === place);
      if (matchingOption) {
        const isSelected = selectedOptions.value.find(it => it.id === matchingOption.id);
        if (!isSelected)
          selectedOptions.value.push(matchingOption);
      }
    }
  }
}

watch(
  () => props.options,
  () => refreshSelectedOptions(),
);

onBeforeMount(() => {
  refreshSelectedOptions();
});
</script>

<template>
  <div class="custom-select" :aria-expanded="isOpen">
    <button
      :aria-haspopup="true"
      aria-controls="places_dropdown"
      :aria-expanded="isOpen"
      role="combobox"
      class="select-button"
      @click="openDropdown"
    >
      {{ props.placeholder }}<span
        v-if="selectedOptions.length"
        class="checked-count"
      >{{ selectedOptions.length }}</span><span
        class="svg"
        :class="{ rotate: isOpen }"
        aria-hidden="true"
      ><ChevronDown
        :size="20" color="var(--yb-color-black)"
      /></span>
    </button>
    <ul
      v-show="isOpen" id="places_dropdown" ref="dropdown" role="listbox" class="options-list scrollbar"
      :style="position"
    >
      <div
        v-for="(option, index) in props.options"
        :key="index"
        class="option-row"
        tabindex="0"
      >
        <li
          role="option"
          tabindex="0"
          class="option"
          :aria-selected="selectedOptions.some(it => it.name === option.name)"
          @click="selectOption(option)"
          @keydown="selectOption(option)"
        >
          <input
            :id="index.toString()" type="checkbox" :name="option.name" :value="option"
            :checked="selectedOptions.some(it => it.name === option.name)" :aria-describedby="index.toString()"
            :aria-checked="selectedOptions.some(it => it.name === option.name)"
          >
          <label :id="index.toString()" :for="index.toString()">{{ option.translatedName.find((it: any) => it.lang
            === currentLocale)?.value ?? option.name }}<span style="margin-left:4px;">({{ option.count
          }})</span></label>
        </li>
      </div>
    </ul>
  </div>
</template>

<style lang="css" scoped>
.custom-select {
  position: relative;
  width: fit-content;
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-light);
}

.select-button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--yb-spacing-xs) var(--yb-spacing-sm);
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  outline-color: var(--yb-outline);
  cursor: pointer;

  .svg {
    position: relative;
    top: 2px;
    margin-left: var(--yb-spacing-xs);

    &.rotate {
      transform: rotate(180deg);
      top: -2px;
    }

    @media (prefers-reduced-motion: no-preference) {
      transform: rotate(0);
      transition: transform 0.2s ease-out;
      transform-origin: center;
    }
  }

  .checked-count {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: var(--yb-text-xs);
    font-weight: var(--yb-text-bold);
    background-color: var(--yb-color-black);
    color: var(--yb-color-white);
    width: 14px;
    aspect-ratio: 1;
    padding: 2px;
    margin-left: var(--yb-spacing-xs);
    border-radius: var(--yb-radius-full);
  }
}

.options-list {
  position: absolute;
  list-style-type: none;
  padding: var(--yb-spacing-xs);
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
  box-shadow: var(--yb-color-mediumgray) 0 4px 8px 0;
  z-index: 10;

  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    transform: translateY(-10px);
    animation: opening 0.2s ease-out forwards;
  }
}

.scrollbar {
  max-height: 210px;
  overflow-y: scroll;
  scrollbar-width: thin;
  scrollbar-color: var(--yb-color-mediumgray) white;
}

.scrollbar::-webkit-scrollbar {
  width: 10px;
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: var(--yb-radius-sm);
  outline: none;
}

.option {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding-block: var(--yb-spacing-xs);
  padding-right: var(--yb-spacing-sm);
  text-wrap: nowrap;
  color: var(--yb-color-black);
  cursor: pointer;

  & > * {
    pointer-events: none;
  }

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    width: 16px;
    aspect-ratio: 1;
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: 2px;
    background-color: var(--yb-color-white);
    margin-inline: var(--yb-spacing-xs);
    accent-color: var(--yb-color-primary);
  }

  input[type='checkbox']:checked {
    background-color: var(--yb-color-primary);
    border-color: var(--yb-color-primary);
    color: var(--yb-color-white);
  }

  input[type='checkbox']:checked::before {
    content: '\2713';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    text-align: center;
    color: var(--yb-color-white);
    font-size: 16px;
  }
}

.option-row:hover,
.option-row:focus,
.option-row:focus-visible {
  background-color: var(--yb-color-lightgray);
}

@keyframes opening {
  to {
    opacity: 1;
    transform: translateY(0px);
  }
}
</style>
